import { STORAGE_TYPES } from "@swiggy-private/native-bridge";
import { sdkResponse } from "../helpers";
import { ComparisonPlanStorageData } from "../types";
import { isEmpty } from "@swiggy-private/js-utils";
import { DELAYS } from "../constants";
import { useCallback, useEffect, useRef } from "react";
import { NEWRELIC_TABLES, recordNREvent } from "@swiggy-private/checkout-core";

interface PollingActionArgs {
    addSuperToCart: (planId: string, planType?: string) => void;
    maxPollingCount: number;
    appStorageKey: string;
    planInCart: string;
}

export const usePolling = ({ addSuperToCart, maxPollingCount, appStorageKey, planInCart = "" }: PollingActionArgs) => {
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const pollingCount = useRef(0);

    const clearTimerInterval = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        pollingCount.current = 0;

        // Delete any previous instance of plan if present in appStorage
        try {
            sdkResponse(STORAGE_TYPES.DELETE, appStorageKey);
        } catch (error) {
            if (error instanceof Error) {
                recordNREvent({ eventType: NEWRELIC_TABLES.FETCH_APP_STORAGE_DATA, error: error?.message });
            }
        }
    }, [appStorageKey]);

    const startPollingAction = useCallback(() => {
        clearTimerInterval(); // Ensure previous polling is stopped before starting a new one

        intervalRef.current = setInterval(async () => {
            pollingCount.current++;

            try {
                const res = await sdkResponse(STORAGE_TYPES.GET, appStorageKey);
                const storageData = res?.data?.[appStorageKey];
                const data = storageData ? (JSON.parse(storageData) as ComparisonPlanStorageData) : null;

                // Stop polling if:
                // 1. Max count is reached
                // 2. Data is empty
                // 3. Webview is closed
                const shouldStopPolling =
                    (!isEmpty(data) && data?.isWebviewClosed) || pollingCount.current >= maxPollingCount;
                if (shouldStopPolling) {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    const { plan_id, type } = data?.planDetails || {};
                    if (!isEmpty(plan_id) && !isEmpty(type) && String(plan_id) !== String(planInCart)) {
                        addSuperToCart(String(plan_id), type);
                    }

                    clearTimerInterval(); // Stop polling and clear storage
                }
            } catch (error) {
                if (error instanceof Error) {
                    recordNREvent({ eventType: NEWRELIC_TABLES.FETCH_APP_STORAGE_DATA, error: error?.message });
                }
            }
        }, DELAYS.POLLING_INTERVAL);
    }, [addSuperToCart, appStorageKey, clearTimerInterval, maxPollingCount, planInCart]);

    // Cleanup polling when the component unmounts
    useEffect(() => {
        return () => clearTimerInterval();
    }, [clearTimerInterval]);

    return { startPollingAction };
};
